import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/modules/growing-circle/lib/api";
import { Button } from "@/components/ui/button";
import { Info, RefreshCw, Loader2, HelpCircle } from "lucide-react";

// Human-readable labels used ONLY in the Back Office presentation layer.
// Internal tracking IDs (data-section on the live site) are unchanged.
const SECTION_LABELS = {
  __page__:       "Pagina intera",
  header:         "Header · navigazione",
  hero:           "Hero · copertina + Buy Now",
  marquee:        "Banner scorrevole",
  problem:        "La domanda iniziale",
  what:           "Cos'è Self Doubling",
  roots:          "Corpo, Trauma e Memoria",
  energy:         "Energia e Depressione",
  "not-self-help":"Non è self-help",
  truth:          "La verità dentro di te",
  author:         "L'autrice",
  monica:         "Monica",
  "final-cta":    "Call to action finale",
  book:           "Indice del libro",
  footer:         "Footer · contatti",
};
const sectionLabel = (sid) => SECTION_LABELS[sid] || sid || "—";

// Concise formula descriptions shown as tooltips next to metric headers.
// Match the aggregations in backend/heatmap_routes.py::summary().
const METRIC_HELP = {
  reach:    "Visitatori unici che sono rimasti almeno 1 secondo con la sezione visibile ≥50% ÷ visitatori unici della pagina.",
  time:     "Media dei millisecondi con la sezione visibile ≥50%, calcolata PER PAGELOAD che ha contribuito (non per visitatore unico). Solo con scheda attiva, cap 60s per singolo pageload.",
  clicks:   "Numero totale di click su CTA all'interno della sezione (non deduplicato per visitatore).",
  conv:     "Visitatori unici che hanno cliccato almeno una CTA nella sezione ÷ visitatori unici che hanno raggiunto la sezione.",
  buyReach: "Visitatori unici che hanno raggiunto la sezione 'Call to action finale' (final-cta) ÷ visitatori unici della pagina. Misura il raggiungimento della sezione, non il click sul pulsante.",
  buyClick: "Visitatori unici che hanno cliccato il pulsante principale 'Buy Now' della Hero (cta buy_now_main) ÷ visitatori unici della pagina.",
  amzOut:   "Visitatori unici usciti verso qualunque marketplace Amazon (da Buy Now, dal modal o da altre CTA) ÷ visitatori unici della pagina.",
  buyToAmz: "Funnel su STESSA coorte: visitatori unici che hanno cliccato buy_now_main E successivamente generato un'uscita Amazon ÷ visitatori unici che hanno cliccato buy_now_main. Naturalmente compresa fra 0% e 100%.",
};

function InfoTip({ text, testid }) {
  return (
    <span className="relative group inline-flex items-center align-middle ml-1"
          data-testid={testid}>
      <HelpCircle className="w-3 h-3 text-muted-foreground/70 cursor-help" />
      <span className="pointer-events-none absolute z-30 left-1/2 -translate-x-1/2 top-full mt-1
                       hidden group-hover:block w-64 max-w-[16rem] text-[11px] leading-snug
                       bg-black/90 text-cream px-3 py-2 rounded-lg shadow-lg normal-case tracking-normal
                       whitespace-normal">
        {text}
      </span>
    </span>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { key: "today",     label: "Oggi" },
  { key: "7d",        label: "Ultimi 7 giorni" },
  { key: "30d",       label: "Ultimi 30 giorni" },
  { key: "year",      label: "Anno corrente" },
  { key: "12m",       label: "Ultimi 12 mesi" },
  { key: "alltime",   label: "Storico completo" },
];

const VIEWPORTS = [
  { key: "all",     label: "Tutti" },
  { key: "desktop", label: "Desktop" },
  { key: "tablet",  label: "Tablet" },
  { key: "mobile",  label: "Mobile" },
];

const TABS = [
  { key: "clicks", label: "Click" },
  { key: "scroll", label: "Scroll" },
  { key: "time",   label: "Tempo in vista" },
];

const toISO = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setUTCDate(d.getUTCDate() - n); return d; };
const monthsAgo = (n) => { const d = new Date(); d.setUTCMonth(d.getUTCMonth() - n); return d; };
const firstOfYear  = () => { const d = new Date(); return new Date(Date.UTC(d.getUTCFullYear(), 0, 1)); };

function resolveRange(preset) {
  const today = new Date();
  switch (preset) {
    case "today":     return { from: toISO(today),      to: toISO(today) };
    case "7d":        return { from: toISO(daysAgo(6)), to: toISO(today) };
    case "year":      return { from: toISO(firstOfYear()), to: toISO(today) };
    case "12m":       return { from: toISO(monthsAgo(12)), to: toISO(today) };
    case "alltime":   return { from: "alltime",         to: toISO(today) };
    default:          return { from: toISO(daysAgo(29)),to: toISO(today) };
  }
}

const SEL_CLS = "bg-primary text-primary-foreground hover:bg-primary/90 border-transparent shadow-[0_0_0_1px_hsl(var(--primary))]";
const UNSEL_CLS = "bg-transparent text-foreground border-border hover:bg-secondary/40";

function fmtNum(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("it-IT").format(n);
}

function fmtPct(n) {
  if (n == null) return "—";
  return `${new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 }).format(n)}%`;
}

function fmtDuration(ms) {
  if (!ms || ms < 1000) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
}

function Card({ label, value, hint, tone = "default", testid, help }) {
  const toneClasses =
    tone === "accent" ? "border-accent/40 bg-accent/[0.03]" :
    tone === "primary" ? "border-primary/40 bg-primary/[0.03]" :
    "border-border";
  return (
    <div className={`rounded-2xl p-4 border bg-card ${toneClasses}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
        {help && <InfoTip text={help} testid={testid ? `${testid}-tip` : undefined} />}
      </p>
      <p className="font-heading text-2xl">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">{hint}</p>}
    </div>
  );
}

function ClickOverlay({ cells, max, imgRef, imgSize }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !imgSize.w) return;
    c.width = imgSize.w;
    c.height = imgSize.h;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (!cells || cells.length === 0 || max <= 0) return;
    // Draw radial gradients centred on each 2%-bucket cell. Alpha scales
    // with count/max, colour ramps from cyan → yellow → red.
    const cellW = c.width * 0.02;
    const cellH = c.height * 0.02;
    const radius = Math.max(cellW, cellH) * 3;
    cells.forEach((cell) => {
      const cx = (cell.x / 100) * c.width + cellW / 2;
      const cy = (cell.y / 100) * c.height + cellH / 2;
      const intensity = Math.min(1, cell.count / max);
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      const alpha = 0.15 + intensity * 0.55;
      if (intensity < 0.33) {
        g.addColorStop(0, `rgba(56, 189, 248, ${alpha})`);
      } else if (intensity < 0.66) {
        g.addColorStop(0, `rgba(250, 204, 21, ${alpha})`);
      } else {
        g.addColorStop(0, `rgba(239, 68, 68, ${alpha})`);
      }
      g.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = g;
      ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
    });
  }, [cells, max, imgSize]);
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ width: "100%", height: "auto" }}
      data-testid="heatmap-canvas"
    />
  );
}

function ScrollOverlay({ reach, imgSize }) {
  // Render horizontal bands at 25/50/75/100% with reach % labels.
  if (!reach || reach.length === 0) return null;
  const bands = [25, 50, 75, 100];
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="scroll-overlay">
      {bands.map((d) => {
        const row = reach.find((r) => r.depth === d);
        if (!row) return null;
        const top = `${d}%`;
        return (
          <div key={d} className="absolute left-0 right-0" style={{ top }}>
            <div className="border-t-2 border-dashed border-accent/60" />
            <div className="absolute right-2 -top-3 bg-black/70 text-accent text-xs px-2 py-0.5 rounded">
              {d}% → {fmtPct(row.reach_pct)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimeOverlay({ sections, sectionBounds }) {
  // Renders a translucent band over each [data-section] on the stored
  // screenshot. Band height matches the section's captured height_pct,
  // opacity scales with the average time-in-view relative to the maximum
  // among visible sections. Aggregate visualization — not eye tracking.
  if (!sections || sections.length === 0) return null;
  if (!sectionBounds || sectionBounds.length === 0) return null;
  const boundsById = Object.fromEntries(sectionBounds.map((b) => [b.section_id, b]));
  const maxT = Math.max(...sections.map((s) => s.avg_time_in_view_ms || 0), 1);
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="time-overlay">
      {sections.map((s) => {
        const b = boundsById[s.section_id];
        if (!b) return null;
        const alpha = 0.08 + Math.min(0.55, ((s.avg_time_in_view_ms || 0) / maxT) * 0.5);
        const secs = Math.round((s.avg_time_in_view_ms || 0) / 1000);
        return (
          <div key={s.section_id} className="absolute left-0 right-0"
               style={{
                 top: `${b.top_pct}%`,
                 height: `${b.height_pct}%`,
                 background: `linear-gradient(90deg, rgba(234, 179, 8, ${alpha}) 0%, rgba(234, 179, 8, ${alpha * 0.7}) 100%)`,
                 borderTop: "1px dashed rgba(234, 179, 8, 0.6)",
                 borderBottom: "1px dashed rgba(234, 179, 8, 0.6)",
               }}
               data-testid={`time-band-${s.section_id}`}>
            <div className="absolute right-2 top-1 bg-black/70 text-accent text-[11px] px-2 py-0.5 rounded">
              {b.label || s.section_id} · {secs}s medi
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AnalyticsHeatmap() {
  const [preset, setPreset] = useState("30d");
  const [viewport, setViewport] = useState("desktop");
  const [tab, setTab] = useState("clicks");
  const [summary, setSummary] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [scroll, setScroll] = useState(null);
  const [sectionBounds, setSectionBounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const imgRef = useRef(null);

  const range = useMemo(() => resolveRange(preset), [preset]);

  const loadAll = () => {
    setLoading(true);
    const params = { from: range.from, to: range.to, viewport, page: "/" };
    Promise.all([
      api.get("/admin/heatmap/summary", { params }),
      api.get("/admin/heatmap/clicks",
              { params: { ...params, viewport: viewport === "all" ? "desktop" : viewport } }),
      api.get("/admin/heatmap/scroll", { params }),
      api.get("/admin/heatmap/layout-sections",
              { params: { page: "/", viewport: viewport === "all" ? "desktop" : viewport } }),
    ]).then(([s, c, sc, lb]) => {
      setSummary(s.data);
      setClicks(c.data);
      setScroll(sc.data);
      setSectionBounds(lb.data?.sections || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(loadAll, [range.from, range.to, viewport]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post("/admin/heatmap/regenerate", null, { params: { page: "/" } });
      loadAll();
    } catch (e) {
      // handled via toast at app layer if desired
    } finally {
      setRegenerating(false);
    }
  };

  const layoutVersion = clicks?.layout_version || summary?.layout_version;
  const screenshotViewport = viewport === "all" ? "desktop" : viewport;
  // Token-authenticated fetch would be ideal; the admin auth here uses
  // HTTP-only cookies (same-origin), so a plain <img> tag with the API
  // path carries the cookie automatically.
  const screenshotUrl = layoutVersion
    ? `${BACKEND_URL}/api/growing-circle/admin/heatmap/screenshot?page=%2F&viewport=${screenshotViewport}&layout_version=${encodeURIComponent(layoutVersion)}`
    : null;

  const onImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    setImgSize({ w: el.naturalWidth, h: el.naturalHeight });
  };

  const emptyState = !layoutVersion
    ? "Nessuna anteprima disponibile. Premi 'Rigenera anteprima' per crearla."
    : (summary?.visitors === 0
        ? "Dati ancora insufficienti. La visualizzazione apparirà con l'aumento del traffico."
        : null);

  return (
    <div data-testid="admin-heatmap-page" className="pb-20">
      {/* Filters row */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">Pagina</span>
          <span className="px-3 py-1 rounded-full border border-border text-xs">Home /</span>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {PRESETS.map((p) => (
            <Button key={p.key} size="sm"
                    variant="outline"
                    className={`rounded-full text-xs uppercase tracking-[0.14em] h-8 border ${preset === p.key ? SEL_CLS : UNSEL_CLS}`}
                    onClick={() => setPreset(p.key)}
                    data-testid={`heatmap-preset-${p.key}`}>{p.label}</Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">Dispositivo</span>
          {VIEWPORTS.map((v) => (
            <Button key={v.key} size="sm"
                    variant="outline"
                    className={`rounded-full text-xs uppercase tracking-[0.14em] h-8 border ${viewport === v.key ? SEL_CLS : UNSEL_CLS}`}
                    onClick={() => setViewport(v.key)}
                    data-testid={`heatmap-viewport-${v.key}`}>{v.label}</Button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="rounded-full text-xs uppercase tracking-[0.14em] h-8"
                onClick={regenerate} disabled={regenerating}
                data-testid="heatmap-regenerate">
          {regenerating ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
          Rigenera anteprima
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-border mb-6">
        {TABS.map((t) => (
          <button key={t.key}
                  className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                    tab === t.key
                      ? "border-primary text-primary font-semibold"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setTab(t.key)}
                  data-testid={`heatmap-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Privacy note explaining the low-volume threshold. Applies to all
          overlays, sits right above the heatmap so it's always visible. */}
      <div className="flex items-start gap-2 text-[11px] text-muted-foreground mb-4 leading-snug"
           data-testid="heatmap-threshold-note">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-accent/80" />
        <span>Le aree con meno di 3 interazioni non vengono visualizzate.</span>
      </div>

      {loading && <p className="text-xs text-muted-foreground mb-4">Caricamento…</p>}

      {emptyState && (
        <div className="bg-secondary/40 border border-border rounded-2xl p-6 flex items-start gap-2 text-sm text-muted-foreground mb-6"
             data-testid="heatmap-empty-state">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{emptyState}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Main area with screenshot + overlay */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {screenshotUrl ? (
            <div className="relative bg-black" data-testid="heatmap-visual">
              <img
                ref={imgRef}
                src={screenshotUrl}
                onLoad={onImgLoad}
                alt="Anteprima home"
                className="block w-full h-auto"
                data-testid="heatmap-screenshot"
              />
              {tab === "clicks" && (
                <ClickOverlay cells={clicks?.cells || []} max={clicks?.max || 0}
                              imgRef={imgRef} imgSize={imgSize} />
              )}
              {tab === "scroll" && <ScrollOverlay reach={scroll?.reach_ge || []} imgSize={imgSize} />}
              {tab === "time" && <TimeOverlay sections={summary?.sections || []} sectionBounds={sectionBounds.map(b => ({ ...b, label: SECTION_LABELS[b.section_id] || b.label }))} />}
              {/* Legend */}
              {tab === "clicks" && (clicks?.max || 0) > 0 && (
                <div className="absolute bottom-3 left-3 bg-black/70 text-[10px] text-cream px-3 py-2 rounded-lg"
                     data-testid="heatmap-legend">
                  <div className="uppercase tracking-widest mb-1">Concentrazione click</div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-cyan-400/70" /> pochi
                    <span className="w-3 h-3 rounded-full bg-yellow-300/70" /> medi
                    <span className="w-3 h-3 rounded-full bg-red-500/70" /> molti
                  </div>
                  {clicks?.hidden_low_volume > 0 && (
                    <div className="mt-1 opacity-70">
                      {clicks.hidden_low_volume} celle sotto soglia (&lt;3 click) nascoste
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Nessuna anteprima. Genera la prima con "Rigenera anteprima".
            </div>
          )}
        </div>

        {/* Side summary */}
        <aside className="space-y-3">
          <Card label="Visitatori" value={fmtNum(summary?.visitors)} testid="hm-card-visitors" />
          <Card label="Durata media visita"
                value={fmtDuration(summary?.avg_page_duration_ms)}
                hint="Tempo attivo, esclude schede in secondo piano" testid="hm-card-duration" />
          <Card label="Sezione più vista"
                value={sectionLabel(summary?.most_viewed_section?.section_id)}
                hint={summary?.most_viewed_section ? `${fmtPct(summary.most_viewed_section.reach_pct)} raggiungimento` : ""}
                testid="hm-card-top-section" />
          <Card label="Sezione con maggior tempo medio"
                value={sectionLabel(summary?.longest_time_section?.section_id)}
                hint={summary?.longest_time_section ? fmtDuration(summary.longest_time_section.avg_time_in_view_ms) : ""}
                testid="hm-card-longest-time" />
          <Card label="CTA più cliccata"
                value={sectionLabel(summary?.top_cta_section?.section_id)}
                hint={summary?.top_cta_section ? `${fmtNum(summary.top_cta_section.clicks)} click` : ""}
                testid="hm-card-top-cta" />
          <Card label="Raggiungono la CTA finale" value={fmtPct(summary?.buy_now_reached_pct)} tone="primary"
                help={METRIC_HELP.buyReach} testid="hm-card-buy-reached" />
          <Card label="Cliccano Buy Now (Hero)" value={fmtPct(summary?.buy_now_click_pct)} tone="primary"
                help={METRIC_HELP.buyClick} testid="hm-card-buy-clicked" />
          <Card label="Uscite verso Amazon" value={fmtPct(summary?.amazon_outbound_pct)} tone="accent"
                help={METRIC_HELP.amzOut} testid="hm-card-amazon-out" />
          <Card label="Buy → Amazon"        value={fmtPct(summary?.buy_to_amazon_pct)}   tone="accent"
                hint="Chi clicca Buy Now ed esce verso Amazon"
                help={METRIC_HELP.buyToAmz} testid="hm-card-buy-to-amazon" />
        </aside>
      </div>

      {/* Ranked table */}
      <section className="mt-8 bg-card border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="heatmap-section-table">
            <thead className="bg-secondary/30 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Sezione</th>
                <th className="px-4 py-3">
                  Raggiunta %
                  <InfoTip text={METRIC_HELP.reach} testid="tip-reach" />
                </th>
                <th className="px-4 py-3">
                  Tempo medio in vista
                  <InfoTip text={METRIC_HELP.time} testid="tip-time" />
                </th>
                <th className="px-4 py-3">
                  Click
                  <InfoTip text={METRIC_HELP.clicks} testid="tip-clicks" />
                </th>
                <th className="px-4 py-3">
                  Conversione CTA
                  <InfoTip text={METRIC_HELP.conv} testid="tip-conv" />
                </th>
              </tr>
            </thead>
            <tbody>
              {(summary?.sections || []).map((s) => (
                <tr key={s.section_id} className="border-t border-border"
                    data-testid={`heatmap-row-${s.section_id}`}>
                  <td className="px-4 py-2.5">{sectionLabel(s.section_id)}</td>
                  <td className="px-4 py-2.5 tabular-nums">{fmtPct(s.reach_pct)}</td>
                  <td className="px-4 py-2.5 tabular-nums">{fmtDuration(s.avg_time_in_view_ms)}</td>
                  <td className="px-4 py-2.5 tabular-nums">{fmtNum(s.clicks)}</td>
                  <td className="px-4 py-2.5 tabular-nums">{fmtPct(s.cta_conversion_pct)}</td>
                </tr>
              ))}
              {(!summary?.sections || summary.sections.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-xs text-muted-foreground">
                    Nessuna sezione con dati per questo periodo.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-[10px] text-muted-foreground text-center leading-relaxed mt-10 max-w-2xl mx-auto">
        Dati aggregati primo-parte. Nessun indirizzo IP o identificatore individuale è memorizzato.
        Le celle con meno di 3 click sono nascoste per proteggere la riservatezza.
        Non tracciamo l'attività degli utenti dopo l'ingresso su Amazon.
      </p>
    </div>
  );
}
